import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { usePhotoSwipe } from './usePhotoSwipe';
import { processImage, extractExif, convertHeicIfNeeded, isHeicFile } from '../lib/watermark';
import useBobineSounds from './useBobineSounds';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

const useDashboardData = (user, navigate, t) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sounds = useBobineSounds();
  const [products, setProducts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [photosPage, setPhotosPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(null);
  const [paymentMsg, setPaymentMsg] = useState(null);
  const [showNumerisation, setShowNumerisation] = useState(false);
  const [numForm, setNumForm] = useState({ name: user?.name || '', email: user?.email || '', phone: '', description: '', photo_count: '' });
  const [numSubmitted, setNumSubmitted] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState(user?.phone || '');
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);
  const viewerOpenRef = useRef(false);
  useEffect(() => { viewerOpenRef.current = viewerIndex !== null; }, [viewerIndex]);
  const scrollYRef = useRef(0);

  const photosLen = photos.length;
  const [compareData, setCompareData] = useState(null);
  const [comparePos, setComparePos] = useState(50);

  const restoreScrollAfterClose = useCallback(() => {
    const saved = scrollYRef.current;
    setTimeout(() => window.scrollTo(0, saved), 60);
  }, []);

  const coffSwipe = usePhotoSwipe({
    isOpen: viewerIndex !== null,
    onClose: useCallback(() => { setViewerIndex(null); setCompareData(null); setComparePos(50); restoreScrollAfterClose(); if (window.history.state?.viewer) window.history.back(); }, [restoreScrollAfterClose]),
    onPrev: useCallback(() => setViewerIndex(i => i !== null && i > 0 ? i - 1 : i), []),
    onNext: useCallback(() => setViewerIndex(i => i !== null && i < photosLen - 1 ? i + 1 : i), [photosLen]),
    totalPhotos: photosLen,
    currentIndex: viewerIndex ?? 0,
  });

  const [restoring, setRestoring] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restoreSuccess, setRestoreSuccess] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const [photoLimit, setPhotoLimit] = useState(50);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') === 'services' ? 'services' : 'tresors';
  });
  const [activeTip, setActiveTip] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [copiedLink, setCopiedLink] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(user?.plan || null);
  const [sasCount, setSasCount] = useState(0);
  const [showTamis, setShowTamis] = useState(false);
  const [smartTamisMode, setSmartTamisMode] = useState('overview');
  const [notifications, setNotifications] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatchDateModal, setShowBatchDateModal] = useState(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [batchDate, setBatchDate] = useState('');
  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [showExifTip, setShowExifTip] = useState(false);
  const [showEclaireurModal, setShowEclaireurModal] = useState(false);
  const [eclaireurStep, setEclaireurStep] = useState('select');
  const [eclaireurSelectedIds, setEclaireurSelectedIds] = useState(new Set());
  const [eclaireurMessage, setEclaireurMessage] = useState('');
  const [eclaireurLink, setEclaireurLink] = useState('');
  const [eclaireurLoading, setEclaireurLoading] = useState(false);
  const [pendingComments, setPendingComments] = useState([]);
  const [moderationLoading, setModerationLoading] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [totalDuplicates, setTotalDuplicates] = useState(0);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [dupToDelete, setDupToDelete] = useState(new Set());
  const [dupDeleting, setDupDeleting] = useState(false);
  const [activeBanner, setActiveBanner] = useState(null);
  const [murmure, setMurmure] = useState(null);
  const [familyCircle, setFamilyCircle] = useState(null);
  const [familyRole, setFamilyRole] = useState(null);
  const [familyInviteEmail, setFamilyInviteEmail] = useState('');
  const [familyLoading, setFamilyLoading] = useState(false);
  const [familyMsg, setFamilyMsg] = useState(null);
  const [familySharedPhotos, setFamilySharedPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoUploading, setVideoUploading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [activeAlbumId, setActiveAlbumId] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState(null);
  const [albumPickerPhotoId, setAlbumPickerPhotoId] = useState(null);
  const [selectedForAlbum, setSelectedForAlbum] = useState([]);
  const [allianceData, setAllianceData] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [editingCaption, setEditingCaption] = useState('');
  const [eclaireurLinkCopied, setEclaireurLinkCopied] = useState(false);
  const [eclaireurEmail, setEclaireurEmail] = useState('');
  const [eclaireurEmailSending, setEclaireurEmailSending] = useState(false);
  const [eclaireurEmailSent, setEclaireurEmailSent] = useState(false);
  const [eclaireurEmailError, setEclaireurEmailError] = useState('');

  const token = localStorage.getItem('bobine_token');
  const fileInputRef = useRef(null);
  const captionTimer = useRef(null);

  // ── Viewer ──
  const openViewer = useCallback((idx) => {
    scrollYRef.current = window.scrollY;
    setViewerIndex(idx);
    setRotation(0);
    window.history.pushState({ viewer: true }, '');
  }, []);

  const closeViewer = useCallback(() => {
    setViewerIndex(null);
    setCompareData(null);
    setComparePos(50);
    restoreScrollAfterClose();
    if (window.history.state?.viewer) window.history.back();
  }, [restoreScrollAfterClose]);

  // ── Infinite scroll ──
  const loadMorePhotos = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = photosPage + 1;
      const res = await axios.get(`${API}/photos?page=${nextPage}&limit=24`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success') {
        setPhotos(prev => [...prev, ...res.data.photos]);
        setHasMore(res.data.has_more || false);
        setPhotosPage(nextPage);
      }
    } catch { /* silent */ }
    setLoadingMore(false);
  }, [loadingMore, hasMore, photosPage, token]);

  useEffect(() => {
    const handlePopState = () => {
      if (viewerIndex !== null) {
        setViewerIndex(null);
        restoreScrollAfterClose();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [viewerIndex, restoreScrollAfterClose]);

  // ── Photo count ──
  const fetchPhotoCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/photos/count`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success') {
        setPhotoCount(res.data.count);
        setPhotoLimit(res.data.limit);
      }
    } catch { /* silent */ }
  }, [token]);

  // ── Sharing ──
  const canShare = currentPlan === 'heritage' || currentPlan === 'lignees';

  const fetchShareLinks = useCallback(async () => {
    if (!token || !canShare) return;
    try {
      const res = await axios.get(`${API}/sharing/my-links`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success') setShareLinks(res.data.links);
    } catch { /* silent */ }
  }, [token, canShare]);

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else { throw new Error('fallback'); }
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { /* silent */ }
    }
    setCopiedLink(text);
  };

  const createShareLink = async () => {
    setSharingLoading(true);
    try {
      const res = await axios.post(`${API}/sharing/invite`,
        { message: shareMessage || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        setShareLinks(prev => [{ id: res.data.share_token, share_url: res.data.share_url, message: res.data.message, owner_name: res.data.owner_name, active: true, views: 0, created_at: new Date().toISOString() }, ...prev]);
        setShareMessage('');
        copyToClipboard(res.data.share_url);
      }
    } catch (e) { console.error(e); }
    setSharingLoading(false);
  };

  const revokeShareLink = async (shareToken) => {
    try {
      await axios.delete(`${API}/sharing/${shareToken}`, { headers: { Authorization: `Bearer ${token}` } });
      setShareLinks(prev => prev.filter(l => l.id !== shareToken));
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (showShareModal && canShare) fetchShareLinks();
  }, [showShareModal, canShare, fetchShareLinks]);

  // ── Main data load ──
  useEffect(() => {
    const load = async () => {
      try {
        // Isolated: a failure of one must NOT block the others
        axios.get(`${API}/prestige/products`)
          .then(r => { if (r.data.status === 'success') setProducts(r.data.products); })
          .catch(() => {});

        if (token) {
          axios.get(`${API}/photos?page=1&limit=24`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => {
              if (r.data.status === 'success') {
                setPhotos(r.data.photos);
                setHasMore(r.data.has_more || false);
                setPhotosPage(1);
              }
            })
            .catch(e => {
              if (e.response?.status === 401) {
                localStorage.removeItem('bobine_token');
                localStorage.removeItem('bobine_user');
                navigate('/compte');
              }
            });

          axios.post(`${API}/photos/generate-thumbnails`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
          fetchPhotoCount();

          const fetches = [
            axios.get(`${API}/sas/count`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.status === 'success') setSasCount(r.data.count); }).catch(() => {}),
            axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.user?.plan) setCurrentPlan(r.data.user.plan); }).catch(() => {}),
            axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.notifications) setNotifications(r.data.notifications); }).catch(() => {}),
            axios.get(`${API}/eclaireur/comments/pending`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.comments) setPendingComments(r.data.comments); }).catch(() => {}),
            axios.get(`${API}/photos/duplicates`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.status === 'success') { setDuplicateGroups(r.data.duplicate_groups || []); setTotalDuplicates(r.data.total_duplicates || 0); } }).catch(() => {}),
            axios.get(`${API}/family/circle`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.circle) { setFamilyCircle(r.data.circle); setFamilyRole(r.data.circle.role); } }).catch(() => {}),
            axios.get(`${API}/videos/list`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.videos) setVideos(r.data.videos); }).catch(() => {}),
            axios.get(`${API}/albums/list`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.albums) setAlbums(r.data.albums); }).catch(() => {}),
            axios.get(`${API}/referral/my-info`, { headers: { Authorization: `Bearer ${token}` } }).then(r => { if (r.data.status === 'success') setAllianceData(r.data); }).catch(() => {}),
          ];
          await Promise.all(fetches);
        }
        try {
          const bannerRes = await axios.get(`${API}/marketing/banner`);
          if (bannerRes.data.active) setActiveBanner(bannerRes.data);
        } catch { /* silent */ }
        if (token) {
          try {
            const murmRes = await axios.get(`${API}/marketing/murmure/active`, { headers: { Authorization: `Bearer ${token}` } });
            if (murmRes.data.active) setMurmure(murmRes.data);
          } catch { /* silent */ }
        }
      } catch (e) {
        console.error(e);
      }
      finally { setLoading(false); }
    };
    load();
  }, [token, navigate, fetchPhotoCount]);

  // ── Auto-refresh SAS count + photos on tab focus / return from sub-view ──
  useEffect(() => {
    if (!token) return;
    const refreshAll = () => {
      if (document.visibilityState !== 'visible') return;
      // CRITICAL: do NOT refresh photos if the viewer is open — would replace the array
      // and eject the user from the lightbox (especially when current photo is past page 1)
      if (viewerOpenRef.current) return;
      axios.get(`${API}/sas/count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { if (r.data.count !== undefined) setSasCount(r.data.count); })
        .catch(() => {});
      // Rafraîchir aussi les photos affichées (cas: retour de Tri Express, retour Safari, etc.)
      axios.get(`${API}/photos?page=1&limit=24`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          if (r.data.status === 'success') {
            setPhotos(r.data.photos);
            setHasMore(r.data.has_more || false);
            setPhotosPage(1);
          }
        })
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', refreshAll);
    window.addEventListener('focus', refreshAll); // iOS Safari: focus event fires on back-button return
    window.addEventListener('pageshow', refreshAll); // Safari bfcache restore
    return () => {
      document.removeEventListener('visibilitychange', refreshAll);
      window.removeEventListener('focus', refreshAll);
      window.removeEventListener('pageshow', refreshAll);
    };
  }, [token]);

  // ── Payment polling ──
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (payment !== 'success' || !sessionId) return;

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) {
        setPaymentMsg({ type: 'warning', text: 'Vérification en cours. Votre plan sera activé sous peu.' });
        return;
      }
      try {
        const res = await fetch(`${API}/checkout/status/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data.plan_activated) {
          const planName = data.plan_id === 'lignees' ? t('svc_lignees') : t('svc_heritage');
          setPaymentMsg({ type: 'success', planName, isTrial: false, text: `Votre commande ${planName} est confirmée.` });
          setSearchParams({});
          if (token) {
            try {
              const meRes = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
              const meData = await meRes.json();
              if (meData.user) {
                localStorage.setItem('bobine_user', JSON.stringify(meData.user));
                window.dispatchEvent(new Event('bobine-user-updated'));
              }
            } catch { /* silent */ }
          }
          return;
        }
        if (data.status === 'expired') { setPaymentMsg({ type: 'error', text: 'La session a expiré.' }); return; }
        attempts++;
        setTimeout(poll, 2000);
      } catch { setPaymentMsg({ type: 'error', text: 'Erreur de vérification.' }); }
    };
    setPaymentMsg({ type: 'pending', text: 'Vérification du paiement...' });
    poll();
  }, [searchParams, setSearchParams, token]);

  // ── Checkout ──
  const handleCheckout = async (productId) => {
    setRedirecting(productId);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`${API}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, origin_url: window.location.origin }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`Erreur serveur (${res.status})`);
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        setTimeout(() => setRedirecting(null), 3000);
      }
    } catch (e) {
      clearTimeout(timeout);
      setRedirecting(null);
      alert(e.name === 'AbortError'
        ? 'Requête expirée. Désactivez votre bloqueur de publicités et réessayez.'
        : `Erreur : ${e.message}`);
    }
  };

  // ── Photo actions ──
  const toggleBook = async (photoId) => {
    try {
      const res = await axios.patch(`${API}/photos/${photoId}/book`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success') setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, in_book: res.data.in_book } : p));
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/photos/${photoId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setDeleteConfirm(null);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (viewerIndex >= 0 && photos[viewerIndex]) setRotation(photos[viewerIndex].rotation || 0);
  }, [viewerIndex, photos]);

  const handleRotate = async (e) => {
    e.stopPropagation();
    const newRotation = (rotation + 90) % 360;
    setRotation(newRotation);
    const photo = photos[viewerIndex];
    if (photo) {
      try {
        await axios.post(`${API}/photos/${photo.id}/rotation`, { rotation: newRotation }, { headers: { Authorization: `Bearer ${token}` } });
        setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, rotation: newRotation } : p));
      } catch { /* silent */ }
    }
  };

  useEffect(() => {
    if (viewerIndex !== null && photos[viewerIndex]) setEditingCaption(photos[viewerIndex].caption || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerIndex]);

  const saveCaption = useCallback((photoId, caption) => {
    if (captionTimer.current) clearTimeout(captionTimer.current);
    captionTimer.current = setTimeout(async () => {
      try {
        await axios.patch(`${API}/photos/${photoId}`, { caption }, { headers: { Authorization: `Bearer ${token}` } });
        setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, caption } : p));
      } catch (e) { console.error(e); }
    }, 800);
  }, [token]);

  // ── Upload ──
  const handleFileUpload = async (files) => {
    // Élargir le filtre : accepter image/* + HEIC (Samsung peut envoyer file.type='')
    const fileList = Array.from(files).filter(f => {
      if (f.type.startsWith('image/')) return true;
      if (f.type === 'application/octet-stream') return true;
      const name = (f.name || '').toLowerCase();
      return name.endsWith('.heic') || name.endsWith('.heif') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp');
    });
    if (!fileList.length) return;

    // ━━━ Vérification quota AVANT traitement ━━━
    // Bloque dès l'upload si le total (déjà scellés + sélection actuelle) dépasse la limite.
    // Évite de faire passer 200 photos au SAS pour bloquer ensuite au scellement.
    const isUnlimited = !photoLimit || photoLimit < 0;
    if (!isUnlimited) {
      // Tient compte des photos déjà au coffre + celles déjà en SAS (qui finiront au coffre)
      // Note: photoCount = scellées au coffre. SAS = en attente. Les deux comptent vers le quota.
      const totalEngaged = photoCount + sasCount;
      const remaining = Math.max(0, photoLimit - totalEngaged);
      if (remaining === 0) {
        toast.error(
          `Votre forfait offre ${photoLimit} Trésors. Vous en avez déjà ${totalEngaged}. Pour ajouter plus de souvenirs, découvrez nos formules.`,
          { duration: 8000, style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.4)', color: '#F7E6BD' } }
        );
        setShowQuotaModal(true);
        return;
      }
      if (fileList.length > remaining) {
        toast(
          `Vous avez s\u00e9lectionn\u00e9 ${fileList.length} photos mais votre forfait n'autorise que ${remaining} Tr\u00e9sor${remaining > 1 ? 's' : ''} suppl\u00e9mentaire${remaining > 1 ? 's' : ''}. Seules les ${remaining} premi\u00e8res seront trait\u00e9es.`,
          { duration: 9000, style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.4)', color: '#F7E6BD' } }
        );
        // Tronquer la sélection au nombre autorisé
        fileList.length = remaining;
      }
    }

    setUploading(true);
    setProcessedCount(0);
    setTotalFiles(fileList.length);

    // Information utilisateur si HEIC détecté (conversion sera faite en pipeline)
    const heicCount = fileList.filter(isHeicFile).length;
    if (heicCount > 0) {
      toast(`${heicCount} photo${heicCount > 1 ? 's' : ''} HEIC d\u00e9tect\u00e9e${heicCount > 1 ? 's' : ''} \u2014 conversion automatique en cours...`, {
        id: 'heic-convert', duration: 5000,
        style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#F7E6BD' },
      });
    }

    const mobile = window.innerWidth < 768;
    const PROCESS_CONCURRENCY = mobile ? 2 : 8; // Mobile: 2 workers en parallèle pour overlap réseau (HEIC convert + canvas)
    const SEND_BATCH = mobile ? 3 : 13;
    const IMG_MAX = mobile ? 1400 : 1500; // Réduit pour limiter la pression mémoire mobile
    const IMG_QUALITY = mobile ? 0.82 : 0.90;

    let done = 0;
    let totalSaved = 0;
    let totalFailed = 0;
    const failureReasons = {}; // { unsupported_format: 3, timeout: 1, ... }

    // ── Phase 1 : Traitement (HEIC = serveur full pipeline / JPG = canvas client avec fallback serveur) ──
    const allProcessed = [];

    // Pipeline serveur complet (décode + resize + Éclat d'Or + watermark) — utilisé pour :
    //  - les HEIC/HEIF (y compris Samsung Motion Photo avec mime incorrect)
    //  - en fallback automatique quand le navigateur échoue (mémoire saturée, format non reconnu...)
    const processViaServer = async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 90000); // 90s max par photo serveur
      try {
        const res = await fetch(`${API}/watermark/upload`, {
          method: 'POST', body: formData,
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (!res.ok) throw new Error(`watermark upload ${res.status}`);
        const data = await res.json();
        if (data.status !== 'success') throw new Error('watermark failed');
        return { watermarked: data.watermarked || data.original, thumbnail: '' };
      } finally {
        clearTimeout(timer);
      }
    };

    // Erreurs canvas pour lesquelles le serveur peut réussir (format HEIF mal détecté, OOM navigateur, etc.)
    const RECOVERABLE_CLIENT_ERRORS = new Set([
      'unsupported_format', 'canvas_export', 'canvas_crash', 'timeout', 'init_failed', 'unknown',
    ]);

    const processOne = async (rawFile) => {
      try {
        const forceServer = isHeicFile(rawFile);
        let pushed = false;

        if (!forceServer) {
          // JPG/PNG : pipeline canvas client (rapide, pas de réseau supplémentaire)
          const [result, exif] = await Promise.all([processImage(rawFile, IMG_MAX, IMG_QUALITY), extractExif(rawFile)]);
          if (result && result.watermarked) {
            allProcessed.push({ watermarked: result.watermarked, thumbnail: result.thumbnail, filename: rawFile.name, exif: exif || {} });
            pushed = true;
          } else if (!RECOVERABLE_CLIENT_ERRORS.has(result?.error)) {
            // Erreur non récupérable (ex: fichier réellement corrompu) : on remonte directement
            totalFailed++;
            const reason = result?.error || 'unknown';
            failureReasons[reason] = (failureReasons[reason] || 0) + 1;
            pushed = true; // évite le fallback serveur
          }
          // Sinon : on continue vers le fallback serveur
        }

        if (!pushed) {
          // Fallback serveur : décode + watermark côté Pillow (pillow_heif compris)
          try {
            const result = await processViaServer(rawFile);
            const exif = await extractExif(rawFile).catch(() => ({}));
            const cleanName = (rawFile.name || 'photo.jpg').replace(/\.(heic|heif)$/i, '.jpg');
            allProcessed.push({ watermarked: result.watermarked, thumbnail: result.thumbnail, filename: cleanName, exif: exif || {} });
          } catch (e) {
            totalFailed++;
            const reason = e?.message?.includes('413') ? 'too_large'
              : e?.message?.includes('500') ? 'server_error'
              : e?.name === 'AbortError' ? 'timeout'
              : 'server_error';
            failureReasons[reason] = (failureReasons[reason] || 0) + 1;
          }
        }
      } catch (e) {
        totalFailed++;
        failureReasons['exception'] = (failureReasons['exception'] || 0) + 1;
      }
      done++;
      setProcessedCount(done);
      if (done % 3 === 0 || done === fileList.length) {
        toast(`${done}/${fileList.length} trait\u00e9${done > 1 ? 's' : ''}...`, { id: 'upload-progress', duration: 2000 });
      }
    };

    const queue = [...fileList];
    const runWorker = async () => { while (queue.length > 0) await processOne(queue.shift()); };
    const workers = [];
    for (let w = 0; w < Math.min(PROCESS_CONCURRENCY, fileList.length); w++) workers.push(runWorker());
    await Promise.all(workers);

    // ── Phase 2 : Envoi séquentiel par batch (zéro race condition) ──
    const sendBatch = async (batch, retryCount = 0) => {
      if (!batch.length) return;
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 180000);
        const res = await fetch(`${API}/photos/save-batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ photos: batch }),
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (res.status === 403) { navigate('/offres'); return; }
        if (res.ok) {
          const data = await res.json();
          const saved = data.saved_count || 0;
          const requested = data.total_requested || batch.length;
          const failedInBatch = Math.max(0, requested - saved);
          totalSaved += saved;
          if (saved > 0) setSasCount(prev => prev + saved);
          // Comptabiliser les photos perdues côté serveur (kDrive crash, mongo insert fail...)
          if (failedInBatch > 0) {
            totalFailed += failedInBatch;
            const serverReason = data.top_failure_reason || 'server_error';
            failureReasons[serverReason] = (failureReasons[serverReason] || 0) + failedInBatch;
          }
          return;
        }
      } catch { /* timeout or network */ }
      // Retry: split batch into singles on first failure, then exponential backoff
      if (retryCount < 3) {
        const wait = 2000 * Math.pow(2, retryCount);
        await new Promise(r => setTimeout(r, wait));
        if (batch.length > 1 && retryCount === 0) {
          for (const single of batch) await sendBatch([single], retryCount + 1);
        } else {
          await sendBatch(batch, retryCount + 1);
        }
      } else {
        totalFailed += batch.length;
        failureReasons['send_failed'] = (failureReasons['send_failed'] || 0) + batch.length;
      }
    };

    toast(`Envoi de ${allProcessed.length} tr\u00e9sor${allProcessed.length > 1 ? 's' : ''} au Sas...`, { id: 'upload-send', duration: 4000 });

    // Send batches concurrently (2 at a time for speed)
    const batches = [];
    for (let i = 0; i < allProcessed.length; i += SEND_BATCH) {
      batches.push(allProcessed.slice(i, i + SEND_BATCH));
    }
    const SEND_CONCURRENCY = 2;
    const batchQueue = [...batches];
    let batchesSent = 0;
    const runSender = async () => {
      while (batchQueue.length > 0) {
        const batch = batchQueue.shift();
        await sendBatch(batch);
        batchesSent++;
        const sent = Math.min(batchesSent * SEND_BATCH, allProcessed.length);
        toast(`${sent}/${allProcessed.length} envoy\u00e9${sent > 1 ? 's' : ''}...`, { id: 'upload-send', duration: 2000 });
      }
    };
    const senders = [];
    for (let s = 0; s < Math.min(SEND_CONCURRENCY, batches.length); s++) senders.push(runSender());
    await Promise.all(senders);

    if (totalSaved > 0 || done > 0) {
      try {
        const [photoRes, sasRes] = await Promise.all([
          axios.get(`${API}/photos?page=1&limit=24`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/sas/count`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (photoRes.data.status === 'success') { setPhotos(photoRes.data.photos); setHasMore(photoRes.data.has_more || false); setPhotosPage(1); }
        const newSasCount = sasRes.data.count;
        if (newSasCount !== undefined) setSasCount(newSasCount);
        await fetchPhotoCount();
        // Identifier la raison principale d'échec pour le bandeau
        let topReason = null;
        if (totalFailed > 0) {
          let topCount = 0;
          for (const [r, c] of Object.entries(failureReasons)) {
            if (c > topCount) { topCount = c; topReason = r; }
          }
        }
        setUploadSuccess({ count: totalSaved, sasCount: newSasCount || totalSaved, failed: totalFailed, failureReason: topReason });
        sounds.cristal();
      } catch { /* silent */ }
    }
    if (totalFailed > 0) {
      // Identifier la raison principale d'échec
      let topReason = 'unknown';
      let topCount = 0;
      for (const [r, c] of Object.entries(failureReasons)) {
        if (c > topCount) { topCount = c; topReason = r; }
      }
      const reasonLabel = {
        too_large: "Image trop volumineuse — r\u00e9essayez par lots plus petits.",
        timeout: "Le serveur a mis trop de temps \u00e0 r\u00e9pondre. R\u00e9essayez dans un instant.",
        server_error: "Le serveur n'a pas pu traiter ces photos. R\u00e9essayez dans un instant.",
        send_failed: "Connexion interrompue durant l'envoi. R\u00e9essayez.",
        unsupported_format: "Format inconnu ou fichier ab\u00eem\u00e9. R\u00e9essayez avec d'autres photos.",
        unknown: "Un probl\u00e8me est survenu. R\u00e9essayez dans un instant.",
      }[topReason] || "Un probl\u00e8me est survenu. R\u00e9essayez dans un instant.";
      toast(`${totalFailed} photo${totalFailed > 1 ? 's' : ''} non envoyée${totalFailed > 1 ? 's' : ''}`, {
        description: reasonLabel,
        duration: 10000,
        style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#F7E6BD' },
      });
    }
    setUploading(false);
  };

  const handleDashDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDashDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.length) handleFileUpload(e.dataTransfer.files); };

  const handleNumSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact/numerisation`, { ...numForm, photo_count: numForm.photo_count ? parseInt(numForm.photo_count) : null });
      setNumSubmitted(true);
    } catch (e) { console.error(e); }
  };

  // ── Tout Sceller (mass seal SAS → Coffre) ──
  const toutSceller = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/sas/tout-sceller`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success' || res.data.status === 'partial') {
        sounds.cristal();
        toast(`${res.data.sealed_count} Trésor${res.data.sealed_count > 1 ? 's' : ''} scellé${res.data.sealed_count > 1 ? 's' : ''} au Coffre-fort`, { duration: 5000 });
        const [photoRes, sasRes] = await Promise.all([
          axios.get(`${API}/photos?page=1&limit=24`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/sas/count`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (photoRes.data.status === 'success') { setPhotos(photoRes.data.photos); setHasMore(photoRes.data.has_more || false); setPhotosPage(1); }
        if (sasRes.data.count !== undefined) setSasCount(sasRes.data.count);
        await fetchPhotoCount();
        if (sasRes.data.count === 0) setShowTamis(false);
        return res.data;
      } else if (res.data.status === 'quota_reached') {
        toast.error(res.data.message, { duration: 6000 });
        return res.data;
      }
    } catch (e) {
      toast.error('Erreur lors du scellement');
    }
    return null;
  }, [token, sounds, fetchPhotoCount]);

  // ── Refresh after SAS seal (no API call, just data refresh) ──
  const refreshAfterSeal = useCallback(async (sealedCount, ecrinName) => {
    if (sealedCount > 0) {
      const msg = ecrinName
        ? `${sealedCount} Trésor${sealedCount > 1 ? 's' : ''} scellé${sealedCount > 1 ? 's' : ''} dans l'Écrin "${ecrinName}"`
        : `${sealedCount} Trésor${sealedCount > 1 ? 's' : ''} scellé${sealedCount > 1 ? 's' : ''} au Coffre-fort`;
      toast(msg, { duration: 6000 });
    }
    const [photoRes, sasRes, albumsRes] = await Promise.all([
      axios.get(`${API}/photos?page=1&limit=24`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/sas/count`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/albums/list`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
    ]);
    if (photoRes.data.status === 'success') { setPhotos(photoRes.data.photos); setHasMore(photoRes.data.has_more || false); setPhotosPage(1); }
    if (sasRes.data.count !== undefined) setSasCount(sasRes.data.count);
    if (albumsRes?.data?.albums) setAlbums(albumsRes.data.albums);
    await fetchPhotoCount();
    if (sasRes.data.count === 0) setShowTamis(false);
  }, [token, fetchPhotoCount]);

  // ── Trash ──
  const fetchTrash = async () => {
    setTrashLoading(true);
    try {
      const res = await axios.get(`${API}/trash/list`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success') setTrashItems(res.data.items || []);
    } catch { setTrashItems([]); }
    setTrashLoading(false);
  };

  const restoreFromTrash = async (item) => {
    try {
      await axios.post(`${API}/trash/restore/${item.type}/${item.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTrashItems(prev => prev.filter(i => i.id !== item.id));
    } catch {}
  };

  const permanentDelete = async (item) => {
    if (!window.confirm('Supprimer definitivement ? Cette action est irreversible.')) return;
    try {
      await axios.delete(`${API}/trash/${item.type}/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setTrashItems(prev => prev.filter(i => i.id !== item.id));
    } catch {}
  };

  const emptyTrash = async () => {
    if (!window.confirm('Vider toute la corbeille ? Tous les elements seront supprimes definitivement.')) return;
    try {
      await axios.post(`${API}/trash/empty`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTrashItems([]);
    } catch {}
  };

  // ── Batch ops ──
  const toggleSelect = (photoId) => {
    setSelectedIds(prev => { const next = new Set(prev); if (next.has(photoId)) next.delete(photoId); else next.add(photoId); return next; });
  };
  const selectAll = () => setSelectedIds(new Set(photos.map(p => p.id)));
  const deselectAll = () => setSelectedIds(new Set());
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    setBatchLoading(true);
    try {
      await axios.post(`${API}/photos/batch-delete`, { photo_ids: [...selectedIds] }, { headers: { Authorization: `Bearer ${token}` } });
      setPhotos(prev => prev.filter(p => !selectedIds.has(p.id)));
      setPhotoCount(prev => Math.max(0, prev - selectedIds.size));
      exitSelectMode();
      setShowBatchDeleteModal(false);
    } catch (e) { console.error(e); }
    setBatchLoading(false);
  };

  const handleBatchDate = async () => {
    if (selectedIds.size === 0 || !batchDate) return;
    setBatchLoading(true);
    try {
      const dt = new Date(batchDate + 'T12:00:00');
      const iso = dt.toISOString();
      await axios.post(`${API}/photos/batch-update-date`, { photo_ids: [...selectedIds], taken_at: iso }, { headers: { Authorization: `Bearer ${token}` } });
      setPhotos(prev => prev.map(p => selectedIds.has(p.id) ? { ...p, taken_at: iso, year: dt.getFullYear(), month: dt.getMonth() + 1 } : p));
      exitSelectMode();
      setShowBatchDateModal(false);
      setBatchDate('');
    } catch (e) { console.error(e); }
    setBatchLoading(false);
  };

  const handleBatchBook = async (inBook) => {
    if (selectedIds.size === 0) return;
    setBatchLoading(true);
    try {
      await axios.post(`${API}/photos/batch-book`, { photo_ids: [...selectedIds], in_book: inBook }, { headers: { Authorization: `Bearer ${token}` } });
      setPhotos(prev => prev.map(p => selectedIds.has(p.id) ? { ...p, in_book: inBook } : p));
      exitSelectMode();
    } catch (e) { console.error(e); }
    setBatchLoading(false);
  };

  // ── Duplicates ──
  const toggleDupDelete = (photoId) => {
    setDupToDelete(prev => { const next = new Set(prev); if (next.has(photoId)) next.delete(photoId); else next.add(photoId); return next; });
  };

  const handleDeleteDuplicates = async () => {
    if (dupToDelete.size === 0) return;
    setDupDeleting(true);
    try {
      await axios.post(`${API}/photos/batch-delete`, { photo_ids: [...dupToDelete] }, { headers: { Authorization: `Bearer ${token}` } });
      const [photoRes, countRes, dupRes] = await Promise.all([
        axios.get(`${API}/photos?page=1&limit=24`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/photos/count`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/photos/duplicates`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (photoRes.data.status === 'success') { setPhotos(photoRes.data.photos); setHasMore(photoRes.data.has_more || false); setPhotosPage(1); }
      if (countRes.data.count !== undefined) setPhotoCount(countRes.data.count);
      if (dupRes.data.status === 'success') { setDuplicateGroups(dupRes.data.duplicate_groups || []); setTotalDuplicates(dupRes.data.total_duplicates || 0); }
      setDupToDelete(new Set());
      if ((dupRes.data.duplicate_groups || []).length === 0) setShowDuplicatesModal(false);
    } catch (e) { console.error(e); }
    setDupDeleting(false);
  };

  // ── Éclaireur ──
  const openEclaireurModal = () => {
    setEclaireurSelectedIds(new Set());
    setEclaireurMessage('');
    setEclaireurLink('');
    setEclaireurStep('select');
    setShowEclaireurModal(true);
  };

  const toggleEclaireurPhoto = (id) => {
    setEclaireurSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const generateEclaireurLink = async () => {
    if (eclaireurSelectedIds.size === 0) return;
    setEclaireurLoading(true);
    try {
      const res = await axios.post(`${API}/eclaireur/invite`, { photo_ids: [...eclaireurSelectedIds], message: eclaireurMessage }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success') {
        const base = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live') || window.location.origin;
        // Lien partagé = endpoint preview HTML (avec meta OG WhatsApp/iMessage).
        // Le clic redirige automatiquement vers /eclaireur/{link_id}.
        // Cohérent avec le Fil de Soie qui pointe sur /api/og/{token}.
        setEclaireurLink(`${base}/api/eclaireur/preview/${res.data.link_id}`);
        setEclaireurStep('link');
        sounds.cristal();
      }
    } catch (e) { console.error(e); }
    setEclaireurLoading(false);
  };

  const shareOnWhatsApp = async () => {
    const ownerName = user?.name?.split(' ')[0] || '';
    // Extraction robuste du link_id (le dernier segment de l'URL, ignorant query)
    const linkId = (eclaireurLink.split('?')[0] || '').split('/').filter(Boolean).pop();
    // 1) Synchroniser le message courant avec le backend AVANT le partage
    //    (sinon WhatsApp scrape un preview avec message obsolète/vide)
    try {
      await axios.put(
        `${API}/eclaireur/invitations/${linkId}/message`,
        { message: eclaireurMessage || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch { /* non bloquant */ }
    // 2) Cache-buster : force WhatsApp à re-scraper le preview à chaque partage
    //    (sinon il garde le cache d'un partage précédent, parfois sans message ni image)
    const cb = Date.now().toString(36);
    const previewUrl = `${API}/eclaireur/preview/${linkId}?v=${cb}`;
    const shareText = t('ecl_whatsapp_share_msg').replace('{name}', ownerName) + '\n\n' + previewUrl;
    if (navigator.share) {
      navigator.share({ title: `${ownerName} — Bobine`, text: t('ecl_whatsapp_share_msg').replace('{name}', ownerName), url: previewUrl }).catch(() => {
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
      });
    } else { window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank'); }
  };

  const copyEclaireurLink = () => {
    const text = eclaireurLink;
    const fallback = (t) => {
      const ta = document.createElement('textarea');
      ta.value = t; ta.style.position = 'fixed'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); setEclaireurLinkCopied(true); setTimeout(() => setEclaireurLinkCopied(false), 2500); } catch {}
      document.body.removeChild(ta);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => { setEclaireurLinkCopied(true); setTimeout(() => setEclaireurLinkCopied(false), 2500); }).catch(() => fallback(text));
    } else { fallback(text); }
  };

  const sendEclaireurEmail = async () => {
    if (!eclaireurEmail || !eclaireurLink) return;
    setEclaireurEmailSending(true);
    try {
      // Extraction robuste du link_id (dernier segment, ignore query)
      const linkId = (eclaireurLink.split('?')[0] || '').split('/').filter(Boolean).pop();
      // 1) Synchroniser le message courant avec le backend (au cas où il a été
      //    modifié dans l'aperçu après la génération du lien)
      try {
        await axios.put(
          `${API}/eclaireur/invitations/${linkId}/message`,
          { message: eclaireurMessage || '' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch { /* non bloquant */ }
      // 2) Envoyer l'email
      const res = await axios.post(`${API}/eclaireur/send-email/${linkId}`, { email: eclaireurEmail }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success') {
        setEclaireurEmailSent(true);
        sounds.cristal();
        setTimeout(() => setEclaireurEmailSent(false), 4000);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur d'envoi";
      setEclaireurEmailError(msg);
      setTimeout(() => setEclaireurEmailError(''), 5000);
    }
    setEclaireurEmailSending(false);
  };

  // ── Restore ──
  const [restoreQuotaReached, setRestoreQuotaReached] = useState(false);

  const executeRestore = async (photoId) => {
    setRestoring(true);
    setRestoreProgress(0);
    const interval = setInterval(() => { setRestoreProgress(p => p >= 90 ? 90 : p + Math.random() * 10); }, 600);
    try {
      const originalSrc = `${API}/photos/${photoId}/image?token=${token}`;
      const res = await axios.post(`${API}/restore`, { photo_id: photoId }, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
      clearInterval(interval);
      setRestoreProgress(100);
      const photosRes = await axios.get(`${API}/photos?page=1&limit=24`, { headers: { Authorization: `Bearer ${token}` } });
      setPhotos(photosRes.data.photos || []);
      setHasMore(photosRes.data.has_more || false);
      setPhotosPage(1);
      setTimeout(() => { setRestoring(false); setRestoreProgress(0); setCompareData({ originalSrc, restoredId: res.data.restored_photo_id }); }, 500);
    } catch (e) {
      clearInterval(interval);
      setRestoring(false);
      setRestoreProgress(0);
      if (e.response?.status === 403 && e.response?.data?.detail === 'eveil_limit_reached') {
        setRestoreQuotaReached(true);
      } else {
        alert(e.response?.data?.detail || 'Erreur lors de la restauration');
      }
    }
  };

  const handleRestore = async (photoId) => { if (!restoring) executeRestore(photoId); };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const restorePhotoId = params.get('restore_photo');
    const sessionId = params.get('session_id');
    const paymentStatus = params.get('payment');
    if (restorePhotoId && sessionId && paymentStatus === 'success') {
      window.history.replaceState({}, '', '/dashboard');
      axios.get(`${API}/checkout/status/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } }).then(res => {
        if (res.data.payment_status === 'paid' || res.data.status === 'complete') executeRestore(restorePhotoId);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Video upload handler ──
  const handleVideoUpload = async (file) => {
    const MAX_VIDEO_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_VIDEO_SIZE) {
      alert(t('video_too_large') || 'Ce souvenir est un peu trop lourd pour être scellé d\'un coup (max 500 Mo).');
      return;
    }
    setVideoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caption', file.name.replace(/\.[^/.]+$/, ''));
      const res = await fetch(`${API}/videos/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      const d = await res.json();
      if (d.status === 'success') setVideos(prev => [d.video, ...prev]);
      else alert(d.detail || 'Erreur upload video');
    } catch { alert('Erreur reseau'); }
    setVideoUploading(false);
  };

  // ── Styles ──
  // Bouton "Lingot d'Or Bombé" — gradient + relief inset/outset shadows
  const goldBtn = (primary) => ({
    fontFamily: "'Georgia', serif", fontSize: 14, fontWeight: primary ? 600 : 500,
    padding: '12px 28px', borderRadius: 999, cursor: 'pointer',
    letterSpacing: 0.5, transition: 'all 0.25s ease',
    background: primary
      ? 'linear-gradient(145deg, #D4AF37, #B8860B)'
      : 'linear-gradient(145deg, rgba(212,175,55,0.08), rgba(184,134,11,0.04))',
    color: primary ? '#1a1a1a' : '#D4AF37',
    border: primary ? '1px solid rgba(184,134,11,0.85)' : '1px solid rgba(212,175,55,0.4)',
    boxShadow: primary
      ? '0 4px 18px rgba(212,175,55,0.35), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 0 rgba(0,0,0,0.22)'
      : '0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(212,175,55,0.15)',
  });

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: 8,
    fontFamily: "'Georgia', serif", fontSize: 14,
    backgroundColor: 'rgba(212,175,55,0.04)',
    border: '1px solid rgba(212,175,55,0.2)',
    color: '#FAF3E0', outline: 'none',
  };

  const bookCount = photos.filter(p => p.in_book).length;

  return {
    // State
    photos, setPhotos, hasMore, setHasMore, photosPage, setPhotosPage, loadingMore,
    loading, redirecting, paymentMsg, setPaymentMsg,
    showNumerisation, setShowNumerisation, numForm, setNumForm, numSubmitted, setNumSubmitted,
    viewerIndex, setViewerIndex, coffSwipe,
    compareData, setCompareData, comparePos, setComparePos,
    restoring, restoreProgress, restoreSuccess, setRestoreSuccess,
    restoreQuotaReached, setRestoreQuotaReached,
    uploading, processedCount, totalFiles, dragActive, uploadSuccess, setUploadSuccess,
    photoCount, photoLimit, showQuotaModal, setShowQuotaModal,
    activeTab, setActiveTab, activeTip, setActiveTip,
    showShareModal, setShowShareModal, shareLinks, shareMessage, setShareMessage,
    sharingLoading, copiedLink, setCopiedLink,
    currentPlan, sasCount, setSasCount, showTamis, setShowTamis, smartTamisMode, setSmartTamisMode,
    notifications, setNotifications, viewMode, setViewMode,
    selectMode, setSelectMode, selectedIds, toggleSelect,
    showBatchDateModal, setShowBatchDateModal, showBatchDeleteModal, setShowBatchDeleteModal,
    batchDate, setBatchDate, batchLoading,
    showTrash, setShowTrash, trashItems, trashLoading,
    showExifTip, setShowExifTip,
    showEclaireurModal, setShowEclaireurModal, eclaireurStep, eclaireurSelectedIds, setEclaireurSelectedIds,
    eclaireurMessage, setEclaireurMessage, eclaireurLink, eclaireurLoading, eclaireurLinkCopied,
    eclaireurEmail, setEclaireurEmail, eclaireurEmailSending, eclaireurEmailSent, eclaireurEmailError,
    pendingComments, setPendingComments, moderationLoading,
    duplicateGroups, totalDuplicates, showDuplicatesModal, setShowDuplicatesModal,
    dupToDelete, dupDeleting,
    activeBanner, setActiveBanner, murmure, setMurmure,
    familyCircle, setFamilyCircle, familyRole, setFamilyRole,
    familyInviteEmail, setFamilyInviteEmail, familyLoading, setFamilyLoading,
    familyMsg, setFamilyMsg, familySharedPhotos, setFamilySharedPhotos,
    videos, setVideos, videoUploading, playingVideo, setPlayingVideo,
    albums, setAlbums, activeAlbumId, setActiveAlbumId,
    albumPhotos, setAlbumPhotos, albumPickerPhotoId, setAlbumPickerPhotoId,
    selectedForAlbum, setSelectedForAlbum,
    allianceData, products,
    deleteConfirm, setDeleteConfirm, rotation, editingCaption, setEditingCaption,
    token, fileInputRef, bookCount, canShare,
    // Actions
    openViewer, closeViewer, loadMorePhotos, fetchPhotoCount,
    copyToClipboard, createShareLink, revokeShareLink,
    handleCheckout, toggleBook, handleDelete, handleRotate, saveCaption,
    handleFileUpload, handleDashDrag, handleDashDrop, handleNumSubmit,
    fetchTrash, restoreFromTrash, permanentDelete, emptyTrash,
    selectAll, deselectAll, exitSelectMode,
    handleBatchDelete, handleBatchDate, handleBatchBook,
    toggleDupDelete, handleDeleteDuplicates,
    openEclaireurModal, toggleEclaireurPhoto, generateEclaireurLink,
    shareOnWhatsApp, copyEclaireurLink, sendEclaireurEmail,
    handleRestore, handleVideoUpload,
    toutSceller, refreshAfterSeal,
    goldBtn, inputStyle, sounds,
  };
};

export default useDashboardData;
